import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { logger } from "appworks/utils/logger";
import { Timer } from "appworks/utils/timer";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { log } from "util";
import { wrapperImplementation } from "../wrapper-implementation";

declare const __DEBUG__: boolean;

/*
TS rewrite of wrapper interface version 1.1.4
Taken from
https://cdn.gameiom.com/gameiom/gameiom-game-wrapper-library/latest/gameWrapper.js
 */
export function injectWrapper() {
    (window as any).gameWrapper = (() => {
        // Prevents multiple setBusy or setIdle being sent to wrapper
        const gameWrapper = {
            gameLoadingStarted() {
                sendToWrapper({
                    action: "gameLoadingStarted"
                });
            },
            gameLoadingEnded() {
                sendToWrapper({
                    action: "gameLoadingEnded"
                });
            },
            // Say to game wrapper that game is busy. Free spins, bonus game or spin are running
            setBusy() {
                sendToWrapper({
                    action: "gameRoundStarted"
                });
            },
            // Say to game wrapper that game is in a feature (bonus, freespins), should be called BEFORE setBusy
            enableFeatureMode() {
                sendToWrapper({
                    action: "enableFeatureMode"
                });
            },
            // Say to game wrapper that game is finished with a feature (bonus, freespins), should be called AFTER setIdle
            disableFeatureMode() {
                sendToWrapper({
                    action: "disableFeatureMode"
                });
            },
            /**
             * Say to game wrapper that game is in Idle state. Game wrapper will be able to show some screens
             *
             * Setting EXTERNAL_DISABLE disables the UI. Very often that wrapper will immediately respond causing the UI
             * to flash. To work around this I delay setting the flag for a few frames.
             */
            setIdle() {
                let resumeRequested = false;
                Timer.setTimeout(() => {
                    if (!resumeRequested) {
                        uiFlags.set(UIFlag.EXTERNAL_DISABLE, true);
                    }
                }, 60);

                wrapperImplementation.onResume.addOnce(() => {
                    resumeRequested = true;
                    uiFlags.set(UIFlag.EXTERNAL_DISABLE, false);
                });

                sendToWrapper({
                    action: "gameRoundEnded"
                });

/////////////////////////////////
////////////////////////////////
//////////////////////////////////////////////////////////////
/////////////////
//////////////////////////
            },

            loadProgressUpdate(percentValue: number) {

                sendToWrapper({
                    action: "loadProgressUpdate",
                    data: {
                        percent: percentValue
                    }
                });
            },

            // Expects an integer
            winningsUpdate(winningValue: number) {

                if (!gameiomModel.shouldDisplayCredits()) {
                    winningValue = centsToCurrency(winningValue);
                }

                sendToWrapper({
                    action: "winningsUpdate",
                    data: {
                        winnings: winningValue
                    }
                });
            },

            // Expects an integer
            stakeUpdate(totalStake: number) {

                if (!gameiomModel.shouldDisplayCredits()) {
                    totalStake = centsToCurrency(totalStake);
                }

                sendToWrapper({
                    action: "stakeUpdate",
                    data: {
                        stake: totalStake
                    }
                });
            },

            // Expects integars
            balancesUpdate(cashValue: number, bonusValue?: number, balanceFudge?: number) {

                if (cashValue === null) {
                    return;
                }

                if (!gameiomModel.shouldDisplayCredits()) {
                    cashValue = centsToCurrency(cashValue);
                    if (bonusValue) {
                        bonusValue = centsToCurrency(bonusValue);
                    }
                    if (balanceFudge) {
                        balanceFudge = centsToCurrency(balanceFudge);
                    }
                }

                this.balancesUpdateDecimal(cashValue, bonusValue, balanceFudge);
            },

            balancesUpdateDecimal(cashValue: number, bonusValue?: number, balanceFudge?: number) {

                if (cashValue === null) {
                    return;
                }

                const data = {
                    action: "balancesUpdate",
                    data: {
                        balances: {
                            cash: cashValue
                        }
                    }
                };

                if (bonusValue && bonusValue > 0) {
                    (data.data.balances as any).bonus = bonusValue;
                }
                if (balanceFudge && balanceFudge > 0) {
                    (data.data as any).balanceFudge = balanceFudge;
                }
                sendToWrapper(data);
            },

            exitGame() {
                sendToWrapper({
                    action: "exitGame"
                });
            },
            enableAudio() {
                sendToWrapper({
                    action: "enableAudio"
                });
            },
            disableAudio() {
                sendToWrapper({
                    action: "disableAudio"
                });
            },
            gotoAccountsPage() {
                sendToWrapper({
                    action: "gotoAccountsPage"
                });
            },
            gotoDepositPage() {
                sendToWrapper({
                    action: "gotoDepositPage"
                });
            },

            isWrapperActive() {
                sendToWrapper({
                    action: "isWrapperActive"
                });
            },
            getOperatorInformation() {
                sendToWrapper({
                    action: "getOperatorInformation"
                });
            },
            getScreenSize() {
                sendToWrapper({
                    action: "getScreenSize"
                });
            },
            menuDialogueOpened() {
                sendToWrapper({
                    action: "menuDialogueOpened"
                });
            },
            menuDialogueClosed() {
                sendToWrapper({
                    action: "menuDialogueClosed"
                });
            },
            betDialogueOpened() {
                sendToWrapper({
                    action: "betDialogueOpened"
                });
            },
            betDialogueClosed() {
                sendToWrapper({
                    action: "betDialogueClosed"
                });
            },
            autospinDialogueOpened() {
                sendToWrapper({
                    action: "autospinDialogueOpened"
                });
            },
            autospinDialogueClosed() {
                sendToWrapper({
                    action: "autospinDialogueClosed"
                });
            },
            paytableDialogueOpened() {
                sendToWrapper({
                    action: "paytableDialogueOpened"
                });
            },
            paytableDialogueClosed() {
                sendToWrapper({
                    action: "paytableDialogueClosed"
                });
            },
            insufficientFunds() {
                sendToWrapper({
                    action: "insufficientFunds"
                })
            },
            bonusGameRecovering() {
                sendToWrapper({
                    action: "bonusGameRecovering"
                })
            },
            gamePopupShown() {
                sendToWrapper({
                    action: "gamePopupShown"
                })
            },
            gamePopupHidden() {
                sendToWrapper({
                    action: "gamePopupHidden"
                })
            },
            gameError(message: string, stack: string) {
                sendToWrapper({
                    action: "gameError",
                    data: { message, stack }
                })
            },

            /*
            To override
             */

            onWrapperMessage(action: string, data: any) {
                logger.info("Wrapper Message", action, data);
            }
        };

        // send message to wrapper
        function sendToWrapper(data: any) {
            if (!data) {
                return;
            }

            let logString = "Sending to Gameiom Wrapper: " + data.action;
            if (data.data) {
                logString += " " + JSON.stringify(data.data);
            }

            log(logString);

            window.parent.postMessage(JSON.stringify(data), "*");
        }

        // subscribe to messages from game wrapper client
        window.addEventListener("message", (message: any) => {
            let data;
            let action;

            try {
                data = JSON.parse(message.data);
                action = data.action;
            } catch (e) {
                return;
            }

            gameWrapper.onWrapperMessage(action, data);
        }, false);

        function centsToCurrency(input: number) {
            return +(input / 100).toFixed(2);
        }

        return gameWrapper;
    })();
}
